/**
 * Free-text search over the Resource Plans list.
 *
 * Client-side, like the list's other filters: the list is already capped server-side and
 * in hand, so a server round trip per keystroke would buy nothing.
 *
 * Every whitespace-separated term must appear somewhere in the plan (AND across terms),
 * case-insensitively, so "acme jane" finds Acme's plans that Jane is suggested on or already staffed on.
 */

import type { ResourcePlanSummaryView } from "@/schemas/resource-plans";

type SearchablePlan = Pick<
  ResourcePlanSummaryView,
  | "name"
  | "solutionName"
  | "clientAccountName"
  | "ownerName"
  | "crmCsmName"
  | "crmClientPartnerName"
  | "rateBasisLabel"
  | "suggestedResourceNames"
  | "laborCategoryNames"
  | "staffedResourceNames"
>;

/** The text a plan is searchable by, lower-cased and joined. */
function searchableText(plan: SearchablePlan): string {
  return [
    plan.name,
    plan.solutionName,
    plan.clientAccountName,
    plan.ownerName,
    plan.crmCsmName,
    plan.crmClientPartnerName,
    plan.rateBasisLabel,
    ...(plan.suggestedResourceNames ?? []),
    ...(plan.laborCategoryNames ?? []),
    ...(plan.staffedResourceNames ?? []),
  ]
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join("\n")
    .toLowerCase();
}

export function matchesPlanSearch(plan: SearchablePlan, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const text = searchableText(plan);
  return terms.every((term) => text.includes(term));
}
